/**
 * Kendall Layne Studio — Theme JavaScript
 * Vanilla JS, no dependencies
 */

'use strict';

/* ============================================
   UTILITIES
   ============================================ */

const $ = (selector, parent = document) => parent.querySelector(selector);
const $$ = (selector, parent = document) => [...parent.querySelectorAll(selector)];

function formatMoney(cents) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  cents = parseInt(cents, 10);
  const dollars = (cents / 100).toFixed(2);
  return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function debounce(fn, wait = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

function trapFocus(container) {
  const focusable = $$('a,button,input,select,textarea,[tabindex]:not([tabindex="-1"])', container)
    .filter(el => !el.disabled && el.offsetParent !== null);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  function handleKeydown(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  container.addEventListener('keydown', handleKeydown);
  first.focus();
  return () => container.removeEventListener('keydown', handleKeydown);
}

/* ============================================
   ANNOUNCEMENT BAR
   ============================================ */
function initAnnouncementBar() {
  const bar = $('#AnnouncementBar');
  if (!bar) return;

  const dismissed = sessionStorage.getItem('announcement-dismissed');
  if (dismissed) {
    bar.remove();
    return;
  }

  const dismissBtn = bar.querySelector('.announcement-bar-dismiss');
  if (dismissBtn) {
    dismissBtn.addEventListener('click', () => {
      bar.style.height = bar.offsetHeight + 'px';
      requestAnimationFrame(() => {
        bar.style.transition = 'height 0.3s ease, opacity 0.3s ease';
        bar.style.height = '0';
        bar.style.opacity = '0';
        bar.style.overflow = 'hidden';
      });
      setTimeout(() => {
        bar.remove();
        sessionStorage.setItem('announcement-dismissed', '1');
      }, 300);
    });
  }
}

/* ============================================
   HEADER SCROLL BEHAVIOR
   ============================================ */
function initHeaderScroll() {
  const header = $('.site-header');
  if (!header) return;

  const isTransparent = header.classList.contains('header-transparent');

  function updateHeader() {
    const scrollY = window.scrollY;
    if (isTransparent) {
      if (scrollY > 60) {
        header.classList.add('header-scrolled');
        header.classList.remove('header-transparent');
      } else {
        header.classList.remove('header-scrolled');
        header.classList.add('header-transparent');
      }
    }
  }

  window.addEventListener('scroll', updateHeader, { passive: true });
  updateHeader();
}

/* ============================================
   MOBILE MENU
   ============================================ */
function initMobileMenu() {
  const hamburger = $('#HamburgerBtn');
  const drawer = $('#MobileNavDrawer');
  const overlay = $('#MobileNavOverlay');
  const closeBtn = $('#MobileNavClose');
  if (!hamburger || !drawer) return;

  let removeTrap;

  function openMenu() {
    drawer.classList.add('is-open');
    overlay && overlay.classList.add('is-visible');
    hamburger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    removeTrap = trapFocus(drawer);
  }

  function closeMenu() {
    drawer.classList.remove('is-open');
    overlay && overlay.classList.remove('is-visible');
    hamburger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (removeTrap) removeTrap();
    hamburger.focus();
  }

  hamburger.addEventListener('click', () => {
    const isOpen = drawer.classList.contains('is-open');
    isOpen ? closeMenu() : openMenu();
  });

  closeBtn && closeBtn.addEventListener('click', closeMenu);
  overlay && overlay.addEventListener('click', closeMenu);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeMenu();
  });

  // Expandable subnav
  $$('.mobile-nav-expand').forEach(btn => {
    btn.addEventListener('click', e => {
      e.preventDefault();
      const item = btn.closest('.mobile-nav-item');
      if (!item) return;
      const isExpanded = item.classList.contains('is-expanded');
      // Close others
      $$('.mobile-nav-item.is-expanded').forEach(el => el.classList.remove('is-expanded'));
      if (!isExpanded) item.classList.add('is-expanded');
    });
  });
}

/* ============================================
   CART STATE
   ============================================ */
const CartState = {
  cart: null,

  async fetch() {
    const res = await fetch('/cart.js');
    this.cart = await res.json();
    return this.cart;
  },

  async addItem(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add item to cart');
    }
    const item = await res.json();
    await this.fetch();
    return item;
  },

  async updateItem(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!res.ok) throw new Error('Could not update cart');
    this.cart = await res.json();
    return this.cart;
  },

  async removeItem(key) {
    return this.updateItem(key, 0);
  },
};

/* ============================================
   CART DRAWER
   ============================================ */
function initCartDrawer() {
  const drawer = $('#CartDrawer');
  const overlay = $('#CartOverlay');
  const closeBtn = $('#CartDrawerClose');
  const openBtns = $$('[data-open-cart]');
  if (!drawer) return;

  let removeTrap;

  function openDrawer() {
    drawer.classList.add('is-open');
    overlay && overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    removeTrap = trapFocus(drawer);
    renderCartDrawer();
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    overlay && overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
    if (removeTrap) removeTrap();
  }

  openBtns.forEach(btn => btn.addEventListener('click', openDrawer));
  closeBtn && closeBtn.addEventListener('click', closeDrawer);
  overlay && overlay.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
  });

  document.addEventListener('cart:open', openDrawer);
  document.addEventListener('cart:close', closeDrawer);
  document.addEventListener('cart:updated', renderCartDrawer);

  window.openCartDrawer = openDrawer;
  window.closeCartDrawer = closeDrawer;
}

function renderCartDrawer() {
  const drawer = $('#CartDrawer');
  if (!drawer) return;

  CartState.fetch().then(cart => {
    updateCartBubbles(cart.item_count);

    const body = drawer.querySelector('.cart-drawer-body');
    const subtotalEl = drawer.querySelector('.cart-subtotal-value');
    const checkoutBtn = drawer.querySelector('.cart-checkout-btn');

    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer-empty">
          <svg class="cart-drawer-empty-icon" viewBox="0 0 24 24">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          <p class="cart-drawer-empty-text">Your cart is empty</p>
          <a href="/collections/all" class="btn btn-primary" onclick="closeCartDrawer()">Start Shopping</a>
        </div>
      `;
      if (subtotalEl) subtotalEl.textContent = formatMoney(0);
      if (checkoutBtn) checkoutBtn.setAttribute('disabled', 'true');
      return;
    }

    if (checkoutBtn) checkoutBtn.removeAttribute('disabled');

    const itemsHtml = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <a href="${item.url}">
          <img
            class="cart-item-image"
            src="${item.image ? item.image.replace('.jpg', '_80x80_crop_center.jpg') : ''}"
            alt="${item.title}"
            width="80" height="80"
            loading="lazy"
          >
        </a>
        <div class="cart-item-details">
          <a href="${item.url}" class="cart-item-title">${item.product_title}</a>
          ${item.variant_title ? `<span class="cart-item-variant">${item.variant_title}</span>` : ''}
          <div class="cart-item-price-row">
            <div class="qty-input-wrap">
              <button class="qty-btn" data-qty-minus aria-label="Decrease quantity">−</button>
              <input class="qty-input" type="number" value="${item.quantity}" min="0" aria-label="Quantity">
              <button class="qty-btn" data-qty-plus aria-label="Increase quantity">+</button>
            </div>
            <span class="cart-item-price">${formatMoney(item.final_line_price)}</span>
          </div>
          <button class="cart-item-remove" data-remove-item="${item.key}">Remove</button>
        </div>
      </div>
    `).join('');

    body.innerHTML = `<div class="cart-items-list">${itemsHtml}</div>`;

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price);

    // Bind qty / remove
    body.querySelectorAll('.cart-item').forEach(itemEl => {
      const key = itemEl.dataset.key;
      const input = itemEl.querySelector('.qty-input');
      const minusBtn = itemEl.querySelector('[data-qty-minus]');
      const plusBtn = itemEl.querySelector('[data-qty-plus]');
      const removeBtn = itemEl.querySelector('[data-remove-item]');

      const updateQty = debounce(async () => {
        const qty = parseInt(input.value, 10);
        if (isNaN(qty) || qty < 0) return;
        try {
          await CartState.updateItem(key, qty);
          document.dispatchEvent(new Event('cart:updated'));
        } catch (e) {
          console.error(e);
        }
      }, 400);

      minusBtn && minusBtn.addEventListener('click', () => {
        input.value = Math.max(0, parseInt(input.value, 10) - 1);
        updateQty();
      });

      plusBtn && plusBtn.addEventListener('click', () => {
        input.value = parseInt(input.value, 10) + 1;
        updateQty();
      });

      input && input.addEventListener('change', updateQty);

      removeBtn && removeBtn.addEventListener('click', async () => {
        itemEl.style.opacity = '0.4';
        itemEl.style.pointerEvents = 'none';
        try {
          await CartState.removeItem(key);
          document.dispatchEvent(new Event('cart:updated'));
        } catch (e) {
          console.error(e);
          itemEl.style.opacity = '1';
          itemEl.style.pointerEvents = 'auto';
        }
      });
    });
  });
}

function updateCartBubbles(count) {
  $$('[data-cart-count]').forEach(el => {
    el.textContent = count;
    el.dataset.count = count;
  });
}

/* ============================================
   ADD TO CART (AJAX)
   ============================================ */
function initAddToCart() {
  document.addEventListener('submit', async e => {
    const form = e.target;
    if (!form.matches('[data-product-form]')) return;
    e.preventDefault();

    const btn = form.querySelector('[data-atc-btn]');
    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput) return;

    const variantId = variantInput.value;
    const qty = parseInt((form.querySelector('[name="quantity"]') || {}).value || '1', 10);

    if (btn) {
      btn.setAttribute('disabled', 'true');
      btn.classList.add('btn-loading');
      btn.textContent = 'Adding…';
    }

    try {
      await CartState.addItem(variantId, qty);
      document.dispatchEvent(new Event('cart:updated'));

      // Open cart drawer
      if (document.body.dataset.cartType === 'drawer') {
        document.dispatchEvent(new Event('cart:open'));
      }

      if (btn) {
        btn.textContent = 'Added!';
        setTimeout(() => {
          btn.removeAttribute('disabled');
          btn.classList.remove('btn-loading');
          btn.textContent = btn.dataset.label || 'Add to Cart';
        }, 1800);
      }
    } catch (err) {
      console.error(err);
      if (btn) {
        btn.textContent = err.message || 'Error — Try Again';
        btn.removeAttribute('disabled');
        btn.classList.remove('btn-loading');
        setTimeout(() => {
          btn.textContent = btn.dataset.label || 'Add to Cart';
        }, 2500);
      }
    }
  });

  // Quick add from product card
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.setAttribute('disabled', 'true');
    const orig = btn.textContent;
    btn.textContent = '…';

    try {
      await CartState.addItem(variantId);
      document.dispatchEvent(new Event('cart:updated'));
      if (document.body.dataset.cartType === 'drawer') {
        document.dispatchEvent(new Event('cart:open'));
      }
      btn.textContent = '✓';
    } catch (e) {
      console.error(e);
      btn.textContent = '!';
    }

    setTimeout(() => {
      btn.removeAttribute('disabled');
      btn.textContent = orig;
    }, 2000);
  });
}

/* ============================================
   PRODUCT IMAGE GALLERY
   ============================================ */
function initProductGallery() {
  const gallery = $('#ProductGallery');
  if (!gallery) return;

  const mainImg = gallery.querySelector('.product-gallery-main img');
  const thumbs = $$('.product-gallery-thumb', gallery);

  thumbs.forEach((thumb, i) => {
    thumb.addEventListener('click', () => {
      const src = thumb.dataset.imageSrc;
      const srcset = thumb.dataset.imageSrcset;
      if (!src || !mainImg) return;

      mainImg.style.opacity = '0';
      setTimeout(() => {
        mainImg.src = src;
        if (srcset) mainImg.srcset = srcset;
        mainImg.style.opacity = '1';
      }, 150);

      thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });
}

/* ============================================
   VARIANT SELECTOR
   ============================================ */
function initVariantSelector() {
  const productForm = $('[data-product-form]');
  if (!productForm) return;

  const productDataEl = $('#ProductJSON');
  if (!productDataEl) return;

  let product;
  try {
    product = JSON.parse(productDataEl.textContent);
  } catch (e) {
    return;
  }

  const variantIdInput = productForm.querySelector('[name="id"]');
  const priceEl = $('#ProductPrice');
  const comparePriceEl = $('#ProductComparePrice');
  const atcBtn = productForm.querySelector('[data-atc-btn]');
  const availabilityEl = $('#ProductAvailability');

  function getSelectedOptions() {
    return $$('[data-option-selector]', productForm).map(sel => {
      if (sel.tagName === 'SELECT') return sel.value;
      if (sel.type === 'radio' && sel.checked) return sel.value;
      return null;
    }).filter(Boolean);
  }

  function findVariant(selectedOptions) {
    return product.variants.find(v =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );
  }

  function updateVariant(variant) {
    if (!variant) return;

    if (variantIdInput) variantIdInput.value = variant.id;

    // Update price
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
      priceEl.classList.toggle('price-sale', variant.compare_at_price > variant.price);
    }

    if (comparePriceEl) {
      if (variant.compare_at_price > variant.price) {
        comparePriceEl.textContent = formatMoney(variant.compare_at_price);
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    // Update add to cart
    if (atcBtn) {
      if (!variant.available) {
        atcBtn.setAttribute('disabled', 'true');
        atcBtn.textContent = 'Sold Out';
        atcBtn.dataset.label = 'Sold Out';
      } else {
        atcBtn.removeAttribute('disabled');
        atcBtn.textContent = 'Add to Cart';
        atcBtn.dataset.label = 'Add to Cart';
      }
    }

    if (availabilityEl) {
      availabilityEl.textContent = variant.available ? 'In Stock' : 'Out of Stock';
      availabilityEl.dataset.available = variant.available;
    }

    // Update URL
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    // Update images if variant has featured image
    if (variant.featured_image) {
      const gallery = $('#ProductGallery');
      const mainImg = gallery && gallery.querySelector('.product-gallery-main img');
      if (mainImg) {
        mainImg.style.opacity = '0';
        setTimeout(() => {
          mainImg.src = variant.featured_image.src;
          mainImg.style.opacity = '1';
        }, 150);
      }

      // Highlight matching thumb
      const thumbs = $$('.product-gallery-thumb', gallery || document);
      thumbs.forEach(thumb => {
        thumb.classList.toggle('is-active',
          thumb.dataset.imageSrc && thumb.dataset.imageSrc.includes(variant.featured_image.src.split('?')[0])
        );
      });
    }
  }

  function onOptionChange() {
    const selected = getSelectedOptions();
    const variant = findVariant(selected);

    // Update swatch active states
    $$('[data-option-position]', productForm).forEach(group => {
      const pos = parseInt(group.dataset.optionPosition, 10) - 1;
      $$('.swatch-btn', group).forEach(btn => {
        btn.classList.toggle('is-selected', btn.dataset.value === selected[pos]);
        const matchVariant = product.variants.find(v =>
          v.options[pos] === btn.dataset.value &&
          selected.every((opt, i) => i === pos || v.options[i] === opt)
        );
        btn.classList.toggle('is-unavailable', matchVariant ? !matchVariant.available : false);
      });
    });

    if (variant) updateVariant(variant);
  }

  // Swatch buttons
  $$('.swatch-btn', productForm).forEach(btn => {
    btn.addEventListener('click', () => {
      const group = btn.closest('[data-option-position]');
      const pos = group && parseInt(group.dataset.optionPosition, 10) - 1;
      $$('.swatch-btn', group).forEach(b => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');

      // Update hidden radio / select
      const radio = group.querySelector(`input[value="${CSS.escape(btn.dataset.value)}"]`);
      if (radio) radio.checked = true;

      onOptionChange();
    });
  });

  // Dropdown selects
  $$('[data-option-selector]', productForm).forEach(sel => {
    sel.addEventListener('change', onOptionChange);
  });

  // Initialize
  onOptionChange();
}

/* ============================================
   QUANTITY INPUT
   ============================================ */
function initQtyInputs() {
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-qty-action]');
    if (!btn) return;

    const wrap = btn.closest('.qty-input-wrap');
    if (!wrap) return;
    const input = wrap.querySelector('.qty-input');
    if (!input) return;

    const current = parseInt(input.value, 10) || 1;
    const action = btn.dataset.qtyAction;
    const min = parseInt(input.min || '1', 10);

    if (action === 'minus') {
      input.value = Math.max(min, current - 1);
    } else if (action === 'plus') {
      input.value = current + 1;
    }

    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/* ============================================
   ACCORDION
   ============================================ */
function initAccordions() {
  $$('.accordion-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId ? $(`#${panelId}`) : trigger.nextElementSibling;

      // If only one open at a time, close others in same accordion
      const accordion = trigger.closest('.accordion');
      if (accordion) {
        $$('.accordion-trigger[aria-expanded="true"]', accordion).forEach(t => {
          if (t !== trigger) {
            t.setAttribute('aria-expanded', 'false');
            const p = t.getAttribute('aria-controls')
              ? $(`#${t.getAttribute('aria-controls')}`)
              : t.nextElementSibling;
            p && p.classList.remove('is-open');
          }
        });
      }

      trigger.setAttribute('aria-expanded', String(!isExpanded));
      panel && panel.classList.toggle('is-open', !isExpanded);
    });
  });
}

/* ============================================
   HEADER SCROLL SMOOTH
   ============================================ */
function initSmoothScroll() {
  document.addEventListener('click', e => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (href === '#') return;
    const target = $(href);
    if (!target) return;
    e.preventDefault();
    const offset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '80', 10);
    const top = target.getBoundingClientRect().top + window.scrollY - offset - 16;
    window.scrollTo({ top, behavior: 'smooth' });
  });
}

/* ============================================
   SCROLL-DOWN INDICATOR
   ============================================ */
function initHeroScroll() {
  const indicator = $('.hero-scroll-indicator');
  if (!indicator) return;
  indicator.addEventListener('click', () => {
    const hero = indicator.closest('.hero-banner');
    if (!hero) return;
    const next = hero.nextElementSibling;
    if (!next) return;
    next.scrollIntoView({ behavior: 'smooth' });
  });
}

/* ============================================
   LAZY LOADING IMAGES
   ============================================ */
function initLazyImages() {
  const images = $$('img[data-src]');
  if (!images.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const img = entry.target;
      img.src = img.dataset.src;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.addEventListener('load', () => img.classList.add('is-loaded'));
      observer.unobserve(img);
    });
  }, {
    rootMargin: '200px 0px',
  });

  images.forEach(img => observer.observe(img));
}

/* ============================================
   NEWSLETTER FORM
   ============================================ */
function initNewsletterForms() {
  $$('[data-newsletter-form]').forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const emailInput = form.querySelector('input[type="email"]');
      const submitBtn = form.querySelector('[type="submit"]');
      const messageEl = form.querySelector('[data-newsletter-message]');

      if (!emailInput) return;

      const email = emailInput.value.trim();
      if (!email) return;

      if (submitBtn) {
        submitBtn.setAttribute('disabled', 'true');
        submitBtn.textContent = '...';
      }

      try {
        // Shopify newsletter subscription via Customer API
        const res = await fetch('/contact#contact_form', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            form_type: 'customer',
            utf8: '✓',
            'contact[email]': email,
            'contact[tags]': 'newsletter',
          }),
        });

        if (messageEl) {
          messageEl.textContent = 'Thank you for subscribing!';
          messageEl.style.color = 'var(--color-success)';
        }
        emailInput.value = '';
      } catch (err) {
        if (messageEl) {
          messageEl.textContent = 'Something went wrong. Please try again.';
          messageEl.style.color = 'var(--color-error)';
        }
      } finally {
        if (submitBtn) {
          submitBtn.removeAttribute('disabled');
          submitBtn.textContent = 'Subscribe';
        }
      }
    });
  });
}

/* ============================================
   COLLECTION SORT
   ============================================ */
function initCollectionSort() {
  const sortSelect = $('#CollectionSort');
  if (!sortSelect) return;

  sortSelect.addEventListener('change', () => {
    const url = new URL(window.location);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location = url.toString();
  });
}

/* ============================================
   WISHLIST (UI ONLY)
   ============================================ */
function initWishlist() {
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;
    btn.classList.toggle('is-wishlisted');
    const isWishlisted = btn.classList.contains('is-wishlisted');
    btn.setAttribute('aria-label', isWishlisted ? 'Remove from wishlist' : 'Add to wishlist');
  });
}

/* ============================================
   CART PAGE QUANTITY
   ============================================ */
function initCartPage() {
  const cartForm = $('#CartForm');
  if (!cartForm) return;

  cartForm.addEventListener('click', async e => {
    const btn = e.target.closest('[data-cart-qty]');
    if (!btn) return;

    const key = btn.dataset.key;
    const action = btn.dataset.cartQty;
    const input = cartForm.querySelector(`input[data-key="${key}"]`);
    if (!input || !key) return;

    let qty = parseInt(input.value, 10);
    if (action === 'minus') qty = Math.max(0, qty - 1);
    if (action === 'plus') qty++;

    input.value = qty;

    try {
      await CartState.updateItem(key, qty);
      window.location.reload();
    } catch (e) {
      console.error(e);
    }
  });
}

/* ============================================
   INITIALIZE
   ============================================ */
document.addEventListener('DOMContentLoaded', () => {
  initAnnouncementBar();
  initHeaderScroll();
  initMobileMenu();
  initCartDrawer();
  initAddToCart();
  initProductGallery();
  initVariantSelector();
  initQtyInputs();
  initAccordions();
  initSmoothScroll();
  initHeroScroll();
  initLazyImages();
  initNewsletterForms();
  initCollectionSort();
  initWishlist();
  initCartPage();

  // Initial cart count
  CartState.fetch().then(cart => updateCartBubbles(cart.item_count)).catch(() => {});
});
